import { useEffect, useState } from 'react';

export type MeasuredHeight = {
  /** Attach to the element whose height should be tracked. */
  ref: (node: HTMLElement | null) => void;
  /** Rendered height (px), 0 until the first measurement lands. */
  heightPx: number;
};

/**
 * Tracks an element's rendered height across every reflow.
 *
 * Anything sizing a viewport against content that grows and shrinks on
 * its own — a widget expanding, a second one appearing, a card
 * swapping to a taller terminal state — cannot get that height from a
 * constant, so it is read from the element instead.
 *
 * @returns A ref to attach to the measured element, and its height.
 */
export const useMeasuredHeight = (): MeasuredHeight => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [heightPx, setHeightPx] = useState(0);

  useEffect(() => {
    if (!node) {
      setHeightPx(0);
      return;
    }

    // `contentRect` is the content box, so it matches the element's
    // footprint only while the element carries no padding or border.
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setHeightPx(entry.contentRect.height);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [node]);

  return { ref: setNode, heightPx };
};
